import React, { useState, useEffect, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AuthContext } from "../AuthContext";
import "./Login.css";
import Swal from 'sweetalert2';
import { debounce } from 'lodash';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function Login() {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState(""); // For login
  const [password, setPassword] = useState("");
  const [registerEmail, setRegisterEmail] = useState(""); // For registration
  const [firstName, setFirstName] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordFeedback, setPasswordFeedback] = useState("");
  const [rateLimited, setRateLimited] = useState(false);
  const navigate = useNavigate();
  const { login, isLoggedIn, userInfo, refreshCsrfToken, csrfToken, authFetch } = useContext(AuthContext);

  useEffect(() => {
    if (!csrfToken) {
      refreshCsrfToken();
    }
  }, [csrfToken, refreshCsrfToken]);

  useEffect(() => {
    if (isLoggedIn && userInfo) {
      const isUser = ['user'].includes(userInfo.role);
      if (isUser) {
        navigate('/dashboard');
      }
    }
  }, [isLoggedIn, userInfo, navigate]);

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);

    if (!passwordVisible) {
      setTimeout(() => {
        setPasswordVisible(false);
      }, 2000);
    }
  };

  const toggleForm = (isRegister) => {
    setShowRegisterForm(isRegister);
    setErrorMessage("");
    setSuccessMessage("");
    setUsername("");
    setEmail("");
    setPassword("");
    setRegisterEmail("");
    setFirstName("");
    setPasswordStrength(0);
    setPasswordFeedback("");
    setRateLimited(false);
  };

  const checkPasswordStrength = debounce((password) => {
    if (!password) {
      setPasswordStrength(0);
      setPasswordFeedback("");
      return;
    }

    let score = 0;

    if (password.length >= 6) score += 1;
    if (password.length >= 10) score += 1;

    if (/[0-9]/.test(password)) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;

    score = Math.min(score, 4);

    setPasswordStrength(score);

    if (score <= 1) {
      setPasswordFeedback("Consider adding numbers or making it longer.");
    } else if (score === 2) {
      setPasswordFeedback("Password could be stronger");
    } else {
      setPasswordFeedback("Good password strength!");
    }
  }, 300);

  useEffect(() => {
    checkPasswordStrength(password);

    return () => {
      checkPasswordStrength.cancel();
    };
  }, [password, checkPasswordStrength]);

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (rateLimited) {
      setErrorMessage("Too many login attempts. Please try again later.");
      return;
    }

    if (!validateEmail(email)) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const credentials = {
        Username: email, // Send email as Username field for backend compatibility
        Password: password
      };

      await login(credentials, "user");
      navigate("/dashboard");
    } catch (error) {
      if (error.status === 429) {
        setRateLimited(true);
        setErrorMessage("Too many login attempts. Please try again later.");
      } else if (error.message) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage("Connection error or invalid credentials. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setErrorMessage("");

    if (!username || !password || !registerEmail || !firstName) {
      setErrorMessage("All fields are required.");
      setLoading(false);
      return;
    }

    if (!validateEmail(registerEmail)) {
      setErrorMessage("Please enter a valid email address.");
      setLoading(false);
      return;
    }

    try {
      const registrationData = {
        username,
        password,
        email: registerEmail,
        firstName
      };

      const response = await authFetch(`${API_BASE_URL}/Users/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-XSRF-TOKEN": csrfToken
        },
        body: JSON.stringify(registrationData)
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 429) {
          setErrorMessage("Too many registration attempts. Please try again later.");
        } else if (data.message) {
          setErrorMessage(data.message);
        } else if (data.errors) {
          const firstErrorKey = Object.keys(data.errors)[0];
          setErrorMessage(data.errors[firstErrorKey].join(". "));
        } else {
          setErrorMessage("Registration failed. Please try again.");
        }
      } else {
        setSuccessMessage("Registration successful! You can now log in.");

        Swal.fire({
          title: 'Success!',
          text: 'Registration successful!',
          icon: 'success',
          timer: 1500,
          confirmButtonColor: '#e96734',
          confirmButtonText: 'OK'
        }).then(() => {
          toggleForm(false);
          setRegisterEmail("");
          setFirstName("");
          setPassword("");
        });
      }
    } catch (error) {
      if (error.status === 429) {
        setErrorMessage("Too many registration attempts. Please try again later.");
      } else {
        setErrorMessage("Registration failed. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  const renderPasswordStrengthBar = () => {
    if (!password || typeof passwordStrength !== 'number') {
      return null;
    }

    const containerStyle = {
      width: '100%',
      marginTop: '10px',
      marginBottom: '10px'
    };

    const barStyle = {
      display: 'flex',
      height: '8px',
      borderRadius: '4px',
      overflow: 'hidden',
      backgroundColor: '#e0e0e0',
      marginBottom: '5px'
    };

    const segmentStyle = {
      flex: 1,
      margin: '0 2px'
    };

    const strengthColors = [
      '#ff4d4d', // very weak - red
      '#ffaa00', // weak - orange
      '#ffff00', // fair - yellow
      '#00cc00', // good - green
      '#00aaff'  // strong - blue
    ];

    const feedbackStyle = {
      fontSize: '12px',
      color: '#666',
      marginTop: '5px'
    };

    return (
      <div style={containerStyle}>
        <div style={barStyle}>
          {[...Array(5)].map((_, index) => (
            <div
              key={index}
              style={{
                ...segmentStyle,
                backgroundColor: index < passwordStrength + 1 ? strengthColors[index] : 'transparent',
              }}
            />
          ))}
        </div>
        {passwordFeedback && (
          <div style={feedbackStyle}>
            {passwordFeedback}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="user-login-container">
        <div className="user-login-loading">
          <div className="user-login-spinner"></div>
          <p>Processing your request...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-login-container">
      <div className="user-login-wrapper">
        <div className="user-login-mobile-form-container">
          {/* Register Form Section */}
          {showRegisterForm ? (
            <div className="user-login-form-box">
              <form onSubmit={handleRegisterSubmit} className="user-login-form">
                <h1>Create Account</h1>
                <span>Create an account to enjoy more features</span>

                {errorMessage && (
                  <div className="user-login-error">{errorMessage}</div>
                )}

                {successMessage && (
                  <div className="user-login-success">{successMessage}</div>
                )}

                <div className="user-login-input-group">
                  <input
                    type="text"
                    className="user-login-input"
                    placeholder="Username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    autoComplete="username"
                  />

                  <div className="user-login-password-field">
                    <input
                      type={passwordVisible ? "text" : "password"}
                      className="user-login-input"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="user-login-password-toggle"
                      onClick={togglePasswordVisibility}
                    >
                      {passwordVisible ? "Hide" : "Show"}
                    </button>
                  </div>

                  {password && renderPasswordStrengthBar()}

                  <input
                    type="email"
                    className="user-login-input"
                    placeholder="Email"
                    value={registerEmail}
                    onChange={(e) => setRegisterEmail(e.target.value)}
                    required
                    autoComplete="email"
                  />

                  <input
                    type="text"
                    className="user-login-input"
                    placeholder="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    autoComplete="given-name"
                  />
                </div>

                <button type="submit" className="user-login-button">
                  Register Now
                </button>

                <div className="user-login-mobile-switch">
                  <p>Already have an account?</p>
                  <button
                    type="button"
                    className="user-login-text-button"
                    onClick={() => toggleForm(false)}
                  >
                    Sign In
                  </button>
                </div>
              </form>
            </div>
          ) : (
            // Sign In Form Section
            <div className="user-login-form-box">
              <form onSubmit={handleSubmit} className="user-login-form">
                <h1>Sign In</h1>
                <span>Sign in to your account</span>

                {errorMessage && (
                  <div className="user-login-error">{errorMessage}</div>
                )}

                {rateLimited && (
                  <div className="user-login-error">
                    Account temporarily locked due to too many failed attempts.
                    Please try again later.
                  </div>
                )}

                <div className="user-login-input-group">
                  <input
                    type="email"
                    className="user-login-input"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    disabled={rateLimited}
                  />

                  <div className="user-login-password-field">
                    <input
                      type={passwordVisible ? "text" : "password"}
                      className="user-login-input"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                      disabled={rateLimited}
                    />
                    <button
                      type="button"
                      className="user-login-password-toggle"
                      onClick={togglePasswordVisibility}
                      disabled={rateLimited}
                    >
                      {passwordVisible ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  className="user-login-button"
                  disabled={rateLimited}
                >
                  SIGN IN
                </button>

                <Link to="/forgot-password" className="user-login-text-button-2">
                  Forgot Password?
                </Link>

                <div className="user-login-mobile-switch">
                  <p>Don't have an account?</p>
                  <button
                    type="button"
                    className="user-login-text-button"
                    onClick={() => toggleForm(true)}
                  >
                    Register Now
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Login;